import { feathers } from '@/feathers-client';
import customErrors from '@/Core/config/errors';
import axios from 'axios';
import { DEFAULT_UNSET_OPTION_NAME, HIDE_ERROR_AFTER_MS } from '@/Core/utilities/index';
import { createTagsString } from '../utilities';
import { FB_CUSTOM_AUDIENCE_FIELDS } from '../config/facebook-payload-fields';

const NOTIFY_MENTIONED_USER = 'notify_mentioned_user';
const CONTACT_OWNER_CUSTOM_FIELDS_LIMIT = 10;
const DEFAULT_ITEM = {
  text: DEFAULT_UNSET_OPTION_NAME,
  value: 0,
};

/**
 * Contacts vuex store
 */
export default {
  namespaced: true,
  state: {
    updateContactErrorCode: 0,
    updateContactErrorMessage: '',
    updateContactError: false,
    findContactError: '',
    hasError: false,
    error: null,
    currentUser: {},
    currentUserSubAccount: {},
    contactInProgress: false,
    modalRunningCampaign: {
      show: false,
      infos: [],
    },
    contact: {},
    emailListsByContact: {},
    addressTypes: [],
    sources: [],
    contactTypes: [],
    hotnesses: [],
    removedFromStaticLists: false,
    removedFromStaticListsHistory: [],
    // todo cc - hard coded until v2 endpoint is available
    statuses: [
      DEFAULT_UNSET_OPTION_NAME,
      'New',
      'Prospecting',
      'Active',
      'Future',
      'Closed/Inactive',
      'Non-Client',
      'Soon',
    ],
    // all tags created by the user
    tags: [],
    tagCreatedSuccess: null,
    tagCreatedError: null,
    // all the subaccount users the contact is shared with
    sharedUsers: [],
    sharedUserIds: [],
    broker: {},
    isCallingContact: false,
    callContactError: false,
    callContactErrorMessage: '',
    notifyMentionedUserError: '',
    contactProfileUnsavedFields: [],
    isHotnessesLoading: false,
    isSourcesLoading: false,
    isContactTypesLoading: false,
    contactOwnerCustomFields: [],
    contactOwnerCustomFieldsTotal: 0,
    isContactOwnerCustomFieldsLoading: false,
    showCreateModal: false,
    showRelationField: false,
    contactSelected: 0,
    contactChat: {},
    contactMixinIsInitialized: false,
  },
  getters: {
    // add subaccount settings to current user object to access permissions
    currentUserInfo: state => {
      return Object.assign(state.currentUser, {
        subAccount: state.currentUserSubAccount,
      });
    },
    hasStaticListSyncedWithFacebook: state => {
      return state.emailListsByContact.some(list => list.fb_user_type_id);
    },
    currentContact: state => {
      return state.contact;
    },
    hotnessesWithRemoveOption(state) {
      return [
        {
          ...DEFAULT_ITEM,
          text: 'Remove Hotness',
          description: 'Removes the hotness from the selected contact(s)',
        },
        ...state.hotnesses,
      ];
    },
    hotnessesWithDefaultItem(state) {
      return [
        {
          ...DEFAULT_ITEM,
          color: null,
        },
        ...state.hotnesses,
      ];
    },
    sourcesWithRemoveItem(state) {
      return [
        {
          id: 0,
          name: 'Remove Source',
          description: 'Removes the source from the selected contact(s)',
        },
        ...state.sources,
      ];
    },
    sourcesWithDefaultItem(state) {
      const mappedSources = state.sources.map(source => {
        return { text: source.name, value: source.id };
      });
      return [DEFAULT_ITEM, ...mappedSources];
    },
    contactTypesWithRemoveItem(state) {
      return [
        {
          ...DEFAULT_ITEM,
          text: 'Remove Contact Type',
          description: 'Remove Contact Type from the selected contact(s)',
        },
        ...state.contactTypes,
      ];
    },
    contactTypesWithDefaultItem(state) {
      return [DEFAULT_ITEM, ...state.contactTypes];
    },
    currentContactFullHomeAddress(state) {
      const { addresses } = state.contact;
      if (!addresses || addresses.length === 0) return '';

      const homeAddress = addresses.find(a => a.type === 'Home');
      if (!homeAddress) return '';

      const {
        street_address_1: street1,
        street_address_2: street2,
        city,
        state: addressState,
        zip,
      } = homeAddress;
      const fullAddress = [street1, street2, city, addressState, zip].join(' ').trim();
      return fullAddress.replace(/\s+/, ' ');
    },
    shouldShowMoreCustomFields(state) {
      return state.contactOwnerCustomFields.length < state.contactOwnerCustomFieldsTotal;
    },
    shouldShowLessCustomFields(state) {
      return state.contactOwnerCustomFields.length > CONTACT_OWNER_CUSTOM_FIELDS_LIMIT;
    },
  },
  mutations: {
    setUpdateContactError(state, error) {
      state.updateContactErrorMessage = error.message;
      state.updateContactError = true;
      state.updateContactErrorCode = error.code;
    },
    resetUpdateContactErrors(state) {
      state.updateContactErrorMessage = '';
      state.updateContactError = false;
      state.updateContactErrorCode = 0;
    },
    setFindContactError(state, errorMessage) {
      state.findContactError = errorMessage;
    },
    setError(state, bool) {
      state.hasError = bool;
    },
    setContactProfileUnsavedFields(state, newField) {
      const unsavedFields = state.contactProfileUnsavedFields.filter(
        field => field.id !== newField.id,
      );
      state.contactProfileUnsavedFields = [...unsavedFields, newField];
    },
    setCurrentUserSubAccount(state, subAccount) {
      delete subAccount.user; // deleted because is redundant info
      state.currentUserSubAccount = subAccount;
    },
    setCurrentUser(state, currentUser) {
      state.currentUser = currentUser;
    },
    setContactInProgress(state, boolean) {
      state.contactInProgress = boolean;
    },
    toggleModalRunningCampaign(state, info) {
      state.modalRunningCampaign = {
        infos: info ? info.data.data : [],
        show: !state.modalRunningCampaign.show,
      };
    },
    // set all users a contact is currently shared with
    setSharedUsers(state, sharedUsers) {
      state.sharedUserIds = sharedUsers.map(sharedUser => sharedUser.user.id);
      state.sharedUsers = sharedUsers.map(sharedUser => {
        return {
          text: `${sharedUser.user.first_name} ${sharedUser.user.last_name}`,
          value: sharedUser.user.id,
        };
      });
    },
    setSources(state, sources) {
      state.sources = sources;
    },
    setContactTypes(state, contactTypes) {
      state.contactTypes = contactTypes.map(contactType => {
        return { text: contactType.name, value: contactType.id };
      });
    },
    setHotnesses(state, hotnesses) {
      state.hotnesses = hotnesses.map(hotness => {
        return { text: hotness.name, value: hotness.id, color: hotness.color };
      });
    },
    setTags(state, tags) {
      state.tags = tags.map(tag => tag.content);
    },
    setAddressTypes(state, types) {
      state.addressTypes = types.data.map(type => type.name);
    },
    setContact(state, contact) {
      contact.type_id = contact.type.id || 0; // TODO MW - fix this in backend
      contact.source_id = contact.source_id || 0; // TODO MW - fix this
      contact.status = contact.status || DEFAULT_UNSET_OPTION_NAME;
      contact.source = contact.source || DEFAULT_UNSET_OPTION_NAME;
      contact.shared_users = state.sharedUsers;
      state.contact = contact;
    },
    setContactChat(state, contact) {
      contact.type_id = contact.type.id || 0;
      contact.source_id = contact.source_id || 0;
      contact.status = contact.status || DEFAULT_UNSET_OPTION_NAME;
      contact.source = contact.source || DEFAULT_UNSET_OPTION_NAME;
      contact.shared_users = state.sharedUsers;
      state.contactChat = contact;
    },
    setEmailListsByContact(state, emailLists = []) {
      state.emailListsByContact = emailLists;
    },
    setCustomField(state, customField) {
      state.contact.custom_fields.push(customField);
    },
    setComment(state, comment) {
      state.contact.comment = comment;
    },
    setAddress(state, address) {
      const { contact } = state;
      const index = contact.addresses.findIndex(addr => addr.id === address.id);
      if (index < 0) {
        contact.addresses.push(address);
      } else {
        contact.addresses.splice(index, 1, address);
      }
    },
    unsetAddress(state, addressId) {
      const { contact } = state;
      const index = contact.addresses.findIndex(addr => addr.id === addressId);
      contact.addresses.splice(index, 1);
    },
    unsetCustomField(state, customFieldId) {
      const { contact } = state;
      const index = contact.custom_fields.findIndex(field => field.id === customFieldId);
      contact.custom_fields.splice(index, 1);
    },
    createTag(state, tag) {
      state.tags.unshift(tag.content);
      state.tagCreatedSuccess = `${tag.content} Tag Created`;
    },
    setCreateTagError(state, message) {
      state.tagCreatedError = `Tag error ${message}`;
    },
    setCreateCustomFieldError(state, message) {
      state.error = `Custom Field error ${message}`;
    },
    setFindError(state, { service, message }) {
      state.error = `${service}: ${message}`;
    },
    hideFindError(state) {
      state.error = null;
    },
    hideSaveError(state) {
      state.error = null;
    },
    createHotness(state, hotness) {
      state.hotnesses.push({ text: hotness.name, value: hotness.id, color: hotness.color });
    },
    createSource(state, source) {
      state.sources.push({ text: source.name, value: source.id });
    },
    createContactType(state, contactType) {
      state.contactTypes.push({ text: contactType.name, value: contactType.id });
    },
    setBroker(state, session) {
      state.broker = session;
    },
    setIsCallingContact(state, status = true) {
      state.isCallingContact = status;
    },
    setCallContactError(state, payload) {
      state.isCallingContact = false;
      state.callContactError = payload.error;
      state.callContactErrorMessage = payload.message;
    },
    setFindEmailListByContactError(state, payload) {
      state.error = payload;
    },
    setRemoveContactFromEmailListError(state, payload) {
      state.error = payload;
    },
    setRemovedFromStaticLists(state, boolean) {
      state.removedFromStaticLists = boolean;
    },
    setRemovedFromStaticListsHistory(state, staticList) {
      state.removedFromStaticListsHistory = staticList;
    },
    setNotifyMentionedUserError(state, payload) {
      state.notifyMentionedUserError = payload;
    },
    setIsHotnessesLoading(state, isLoading) {
      state.isHotnessesLoading = isLoading;
    },
    setIsSourcesLoading(state, isLoading) {
      state.isSourcesLoading = isLoading;
    },
    setIsContactTypesLoading(state, isLoading) {
      state.isContactTypesLoading = isLoading;
    },
    setContactOwnerCustomFields(state, contactOwnerCustomFields) {
      state.contactOwnerCustomFields =
        state.contactOwnerCustomFields.concat(contactOwnerCustomFields);
    },
    setContactOwnerCustomFieldsTotal(state, contactOwnerCustomFieldsTotal) {
      state.contactOwnerCustomFieldsTotal = contactOwnerCustomFieldsTotal;
    },
    resetContactOwnerCustomFields(state) {
      state.contactOwnerCustomFields = [];
    },
    setIsContactOwnerCustomFieldsLoading(state, isContactOwnerCustomFieldsLoading) {
      state.isContactOwnerCustomFieldsLoading = isContactOwnerCustomFieldsLoading;
    },
    setShowCreateModal(state, show) {
      state.showCreateModal = show;
    },
    setShowRelationField(state, show) {
      state.showRelationField = show;
    },
    setContactSelected(state, contact) {
      state.contactSelected = contact;
    },
    setContactMixinIsInitialized(state, bool) {
      state.contactMixinIsInitialized = bool;
    },
  },

  actions: {
    // find the user that's currently logged in
    async findCurrentUser({ commit }) {
      const user = await axios.get(`${process.env.VUE_APP_ACCOUNTS_API}/users/me`, {
        withCredentials: true,
      });
      commit('setCurrentUser', user);
      try {
        // find current user's subaccount info, if any
        const subAccount = await feathers.service('sub-accounts').find({
          $paginate: false,
          user_id: user.data[0].id,
        });
        commit('setCurrentUserSubAccount', subAccount.data[0]);
      } catch (err) {
        // todo cc redo this once backend change to get rid of 403 error is merged in
        commit('setCurrentUserSubAccount', []);
        if (err.code >= 500) console.log(err);
      }
    },

    // find all users the contact is currently being shared with
    async findSharedUsers({ commit, state }, contactId) {
      try {
        const sharedUsers = await feathers.service(`contacts/${contactId}/shared-users`).find({
          $pagination: false,
        });
        commit('setSharedUsers', sharedUsers.data);
      } catch (error) {
        commit('setFindError', {
          service: 'Shared Users',
          message: error.message,
        });
        setTimeout(() => {
          commit('hideFindError');
        }, HIDE_ERROR_AFTER_MS);
      }
    },

    // find contact sources
    async loadContactSources({ state, commit }, force = false) {
      if (!force && (state.isSourcesLoading || state.sources.length)) return;

      commit('setIsSourcesLoading', true);
      try {
        const sources = await feathers.service('contact-sources').find({
          query: {
            $paginate: false,
            $sort: { name: 1 },
          },
        });
        commit('setSources', sources);
      } catch (error) {
        commit('setFindError', {
          service: 'Contact Sources',
          message: error.message,
        });
        setTimeout(() => {
          commit('hideFindError');
        }, HIDE_ERROR_AFTER_MS);
      }
      commit('setIsSourcesLoading', false);
    },

    // find contact types
    async loadContactTypes({ state, commit }) {
      if (state.isContactTypesLoading || state.contactTypes.length) return;

      commit('setIsContactTypesLoading', true);
      try {
        const types = await feathers.service('contact-types').find({
          query: {
            $paginate: false,
            $sort: { name: 1 },
          },
        });
        commit('setContactTypes', types);
      } catch (error) {
        commit('setFindError', {
          service: 'Contact Types',
          message: error.message,
        });
        setTimeout(() => {
          commit('hideFindError');
        }, HIDE_ERROR_AFTER_MS);
      }
      commit('setIsContactTypesLoading', false);
    },

    // find contact hotnesses
    async loadContactHotnesses({ state, commit }) {
      if (state.isHotnessesLoading || state.hotnesses.length) return;
      commit('setIsHotnessesLoading', true);
      try {
        const hotnesses = await feathers
          .service('hotnesses')
          .find({ query: { $paginate: false, $sort: { name: 1 } } });
        commit('setHotnesses', hotnesses);
      } catch (error) {
        commit('setFindError', {
          service: 'Hotnesses',
          message: error.message,
        });
        setTimeout(() => {
          commit('hideFindError');
        }, HIDE_ERROR_AFTER_MS);
      }
      commit('setIsHotnessesLoading', false);
    },

    // find tags
    async findTags({ commit }, searchTerm = null) {
      try {
        const query = {
          $paginate: false,
          $sort: { content: 1 },
        };
        if (searchTerm) query.content = { $like: searchTerm };

        const tags = await feathers.service('tags').find({ query });
        commit('setTags', tags);
      } catch (error) {
        commit('setFindError', { service: 'Tags', message: error.message });
        setTimeout(() => {
          commit('hideFindError');
        }, HIDE_ERROR_AFTER_MS);
      }
    },

    // find address types
    async findAddressTypes({ state, commit }) {
      // already loaded
      if (state.addressTypes.length) return;
      try {
        const types = await feathers.service('address-types').find();
        commit('setAddressTypes', types);
      } catch (error) {
        commit('setFindError', {
          service: 'Address Types',
          message: error.message,
        });
        setTimeout(() => {
          commit('hideFindError');
        }, HIDE_ERROR_AFTER_MS);
      }
    },

    // find contact
    async findContact({ state, commit }, { contactId, profileV2 = false, isChat = false }) {
      commit('setContactInProgress', true);
      commit('setFindContactError', '');
      try {
        const contact = await feathers.service('contacts').get(contactId, {
          query: { isProfileV2: profileV2 },
        });
        if (isChat) {
          commit('setContactChat', contact);
        } else {
          commit('setContact', contact);
        }
        commit('setContactInProgress', false);
      } catch (error) {
        commit('setFindContactError', error.message);
      }
    },

    async findEmailListsByContact({ state, commit }, id) {
      try {
        const response = await feathers.service(`static-lists/${id}/lists`).find();
        commit('setEmailListsByContact', response);
      } catch (error) {
        if (error.code === 400) {
          error.message = customErrors[error.code];
        }
        commit('setFindEmailListByContactError', error.message);
      }
    },

    async createCustomField({ commit }, customField) {
      try {
        const newCustomField = await feathers.service('custom-fields').create(customField);
        commit('setCustomField', newCustomField);
        return newCustomField.id;
      } catch (error) {
        commit('setCreateCustomFieldError', error);
      }
    },

    // add a new tag
    async createTag({ commit }, tag) {
      try {
        const newTag = await feathers
          .service('tags')
          .create({ content: tag.replace(/\s{2,}/g, ' ').trim() });
        commit('createTag', newTag);
      } catch (error) {
        commit('setCreateTagError', error);
      }
    },
    // create comment
    async createComment({ commit, state }, comment) {
      try {
        const newComment = await feathers.service('events').create({
          type: 'Contact Comments',
          contact_id: state.contact.id,
          description: comment,
        });
        commit('setComment', newComment);
      } catch (error) {
        commit('setFindError', { service: 'Events', message: error.message });
        setTimeout(() => {
          commit('hideFindError');
        }, HIDE_ERROR_AFTER_MS);
      }
    },
    // create contact address
    async createAddress({ commit, state }, address) {
      const saved = await feathers
        .service(`contacts/${state.contact.id}/addresses`)
        .create(address);
      saved.isNewAddress = true;
      commit('setAddress', saved);
    },
    // create address for a new related contact
    async createAddressForRelatedContact({ commit }, { address, contactId }) {
      try {
        await feathers
          .service(`contacts/${contactId}/addresses`)
          .create(address);
      } catch (error) {
        commit('setFindError', { service: 'Address', message: error.message });
        setTimeout(() => {
          commit('hideFindError');
        }, HIDE_ERROR_AFTER_MS);
      }
    },
    // create comment for new related contact
    async createCommentForRelatedContact({ commit }, { comment, contactId }) {
      try {
        await feathers.service('events').create({
          type: 'Contact Comments',
          contact_id: contactId,
          description: comment,
        });
      } catch (error) {
        commit('setFindError', { service: 'Events', message: error.message });
        setTimeout(() => {
          commit('hideFindError');
        }, HIDE_ERROR_AFTER_MS);
      }
    },
    // update contact address
    async patchAddress({ commit, state }, address) {
      // remove ID as it's not meant to be a patch param property
      const addrId = address.id;
      delete address.id;
      // not supported by front-end yet - TODO MW
      delete address.country;
      const saved = await feathers
        .service(`contacts/${state.contact.id}/addresses`)
        .patch(addrId, address);
      // add ID back in
      const savedAddress = Object.assign({ id: addrId }, saved);
      commit('setAddress', savedAddress);
    },

    // delete contact address
    async removeAddress({ commit, state }, addressId) {
      await feathers.service(`/contacts/${state.contact.id}/addresses`).remove(addressId);
      commit('unsetAddress', addressId);
    },

    // delete custom field
    async removeCustomField({ commit }, customFieldId) {
      await feathers.service('custom-fields').remove(customFieldId);
      commit('unsetCustomField', customFieldId);
    },

    // share contact with a user
    async createSharedUser({ commit, dispatch, state }, params) {
      await feathers.service(`contacts/${state.contact.id}/shared-users`).create(params);
      await dispatch('findSharedUsers', state.contact.id);
      dispatch('findContact', { contactId: state.contact.id });
    },

    // un-share a contact with a user
    async deleteSharedUser({ commit, dispatch, state }, userId) {
      // find & delete contact shared user by user ID
      const sharedUser = await feathers
        .service(`/contacts/${state.contact.id}/shared-users`)
        .find({ query: { user_id: userId } });
      await feathers
        .service(`contacts/${state.contact.id}/shared-users`)
        .remove(sharedUser.data[0].id);
      await dispatch('findSharedUsers', state.contact.id);
      dispatch('findContact', { contactId: state.contact.id });
    },

    async hideModalRunningCampaign({ commit, state, dispatch, getters }, { save, patchedFields }) {
      if (save) {
        patchedFields.update_type = 'bypass-campaign-running-validation';
        await dispatch('patchContact', patchedFields);
      }
      commit('toggleModalRunningCampaign');
    },
    // update contact info
    async patchContact({ commit, state, dispatch, getters }, patchedFields) {
      commit('setContactInProgress', true);
      commit('setError', false);

      // create a copy of patchedFields to prevent side effects
      const params = Object.assign({}, patchedFields);
      const contactId = patchedFields.contact_id || state.contact.id;

      // remove any fields from joined tables
      delete params.addresses;
      delete params.shared_users;
      delete params.contact_id;
      delete params.custom_fields_remove;
      delete params.contacts_relationships;

      const { addresses } = patchedFields;
      // CUD address
      if (addresses && addresses.write.length > 0) {
        for (const address of addresses.write) {
          delete address.isNewAddress;
          if (address.id > 0) {
            const patchAddress = JSON.parse(JSON.stringify(address));
            await dispatch('patchAddress', patchAddress);
          } else {
            delete address.id;
            await dispatch('createAddress', address);
          }
        }
      }

      if (addresses && addresses.remove.length > 0) {
        for (const addressId of addresses.remove) {
          await dispatch('removeAddress', addressId);
        }
      }

      const { custom_fields: customFields, custom_fields_remove: customFieldsRemove } =
        patchedFields;
      if (customFields && customFields.length > 0) {
        for (const customField of customFields) {
          if (customField.id < 0) {
            const { name, data_type } = customField;
            const id = await dispatch('createCustomField', { name, data_type });
            delete customField.data_type;
            delete customField.name;
            customField.id = id;
          }
        }
      }

      if (customFieldsRemove && customFieldsRemove.length > 0) {
        for (const fieldRemove of customFieldsRemove) {
          await dispatch('removeCustomField', fieldRemove);
        }
      }

      const hasContactInfoModified = Object.keys(params).some(key => key !== 'custom_fields');

      if (hasContactInfoModified || params.custom_fields.length) {
        try {
          const savedContact = await feathers.service('contacts').patch(contactId, params);
          commit('setContact', savedContact);
        } catch (error) {
          if (error.message.includes("campaign running based on it's birthday")) {
            commit('toggleModalRunningCampaign', error);
          }
          commit('setError', true);
          commit('setUpdateContactError', error);
          commit('setContactInProgress', false);
          throw error;
        }
      }

      if (getters.hasStaticListSyncedWithFacebook) {
        let needToUpdate = Object.keys(params).some(key =>
          FB_CUSTOM_AUDIENCE_FIELDS.contactFields.includes(key),
        );
        const homeAddresses = addresses.write.filter(address => address.type === 'Home');

        if (!needToUpdate && homeAddresses.length) {
          needToUpdate = homeAddresses.some(address => {
            return Object.keys(address).some(key =>
              FB_CUSTOM_AUDIENCE_FIELDS.addressFields.includes(key),
            );
          });
        }

        if (needToUpdate) {
          try {
            await feathers.service('update-custom-audience-contacts').patch(contactId, {});
          } catch (error) {
            commit('setError', true);
            commit('setUpdateContactError', error);
            commit('setContactInProgress', false);
            throw error;
          }
        }
      }

      const { shared_users: sharedUsers } = patchedFields;
      const sharedUserIds = sharedUsers;
      if (sharedUserIds) {
        // remove any shared users from the DB that were unselected in the UI
        const userIdsToRemove = state.sharedUserIds.filter(id => !sharedUserIds.includes(id));
        userIdsToRemove.forEach(async userId => {
          await dispatch('deleteSharedUser', userId);
        });

        // don't create any shared users that already exists in the DB
        const userIdsToSave = sharedUserIds.filter(id => {
          return !userIdsToRemove.includes(id) && !state.sharedUserIds.includes(id);
        });
        userIdsToSave.forEach(async userId => {
          await dispatch('createSharedUser', { user_id: userId });
        });

        commit('setContactInProgress', false);
      }

      const { contacts_relationships: contactsRelationships } = patchedFields;
      if (contactsRelationships && contactsRelationships.length > 0) {
        await dispatch('patchContactRelationships', contactsRelationships);
      }

      commit('setContactInProgress', false);
    },

    // create new contact
    async createContact({ commit, state, dispatch }, params) {
      commit('setContactInProgress', true);
      commit('setError', false);

      const noCache = !!params.no_cache;
      const contactParams = Object.assign({}, params);
      delete contactParams.addresses;
      delete contactParams.comment;
      delete contactParams.no_cache;
      delete contactParams.relation;

      // convert tags from array to string
      if (contactParams.tags) {
        const tagsContacts = contactParams.tags.map(tag => {
          return tag.replace(/\s{2,}/g, ' ').trim();
        });
        contactParams.tags = createTagsString(tagsContacts);
      }
      let newContact;

      try {
        newContact = await feathers.service('contacts').create(contactParams, {
          headers: { no_cache: noCache },
        });
        if (!state.showRelationField) commit('setContact', newContact);
      } catch (error) {
        commit('setContactInProgress', false);
        throw error;
      }

      const { addresses, comment } = params;
      // create address
      if (addresses) {
        if (state.showRelationField) {
          addresses.forEach(async address => {
            await dispatch('createAddressForRelatedContact', { address: address, contactId: newContact.id });
          });
        } else {
          addresses.forEach(async address => {
            await dispatch('createAddress', address);
          });
        }
      }

      // create comment
      if (comment) {
        if (state.showRelationField) {
          await dispatch('createCommentForRelatedContact', { comment: comment, contactId: newContact.id });
        } else {
          await dispatch('createComment', comment);
        }
      }

      commit('setContactInProgress', false);
      return newContact;
    },

    // create new hotness
    async createHotness({ commit }, hotness) {
      try {
        const newHotness = await feathers.service('hotnesses').create(hotness);
        commit('createHotness', newHotness);
      } catch (error) {
        throw error;
      }
    },

    // create new source
    async createSource({ commit }, source) {
      try {
        const newSource = await feathers.service('contact-sources').create(source);
        commit('createSource', newSource);
      } catch (error) {
        throw error;
      }
    },

    // create new hotness
    async createContactType({ commit }, contactType) {
      try {
        const newContactType = await feathers.service('contact-types').create(contactType);
        commit('createContactType', newContactType);
      } catch (error) {
        throw error;
      }
    },

    async findBroker({ commit }) {
      try {
        const session = await feathers.service('sessions').find();
        commit('setBroker', session.broker ? session.broker : null);
      } catch (error) {
        throw error;
      }
    },

    async makeCall({ commit }, params) {
      try {
        await feathers.service('voice').create({
          contact_id: params.contactId,
          phone: params.phone,
          agent_name: params.agentName,
          type: params.type,
        });
        commit('setIsCallingContact');
      } catch (error) {
        commit('setCallContactError', { error: true, message: error.message });
        setTimeout(() => {
          commit('setCallContactError', { error: false, message: '' });
        }, HIDE_ERROR_AFTER_MS);
      }
    },
    async removeContactFromEmailList({ commit }, { listId, idContact }) {
      try {
        const deletedContact = await feathers
          .service(`/static-lists/${listId}/contacts/${idContact}`)
          .remove(null);
        return deletedContact;
      } catch (error) {
        commit('setRemoveContactFromEmailListError', error.message);
      }
    },
    async notifyMentionedUser({ commit }, params) {
      try {
        await axios.post(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/scheduled/${NOTIFY_MENTIONED_USER}`,
          { users: params },
        );
      } catch (error) {
        commit('setNotifyMentionedUserError', error.message);
      }
    },
    assignRemovedFromStaticListsHistory({ commit }, staticLists) {
      commit('setRemovedFromStaticListsHistory', staticLists);
    },
    async getContactOwnerCustomFields({ commit, state }, contactID) {
      if (state.isContactOwnerCustomFieldsLoading) return;
      try {
        commit('setIsContactOwnerCustomFieldsLoading', true);
        const skip = state.contactOwnerCustomFields.length;
        const { data } = await axios.get(
          `${process.env.VUE_APP_CONTACTS_MS_BASE_URL}/contact/${contactID}/owner/custom-fields?$limit=${CONTACT_OWNER_CUSTOM_FIELDS_LIMIT}&$skip=${skip}`,
        );
        if (data.data) {
          commit('setContactOwnerCustomFields', data.data);
          commit('setContactOwnerCustomFieldsTotal', data.total);
        }
      } finally {
        commit('setIsContactOwnerCustomFieldsLoading', false);
      }
    },
    showLessContactOwnerCustomFields({ commit, state }) {
      if (state.contactOwnerCustomFields.length <= CONTACT_OWNER_CUSTOM_FIELDS_LIMIT) return;

      let endIndex = state.contactOwnerCustomFields.length - CONTACT_OWNER_CUSTOM_FIELDS_LIMIT;
      if (endIndex < CONTACT_OWNER_CUSTOM_FIELDS_LIMIT) {
        endIndex =
          state.contactOwnerCustomFields.length -
          (state.contactOwnerCustomFields.length % CONTACT_OWNER_CUSTOM_FIELDS_LIMIT);
      }

      const slicedArray = state.contactOwnerCustomFields.slice(0, endIndex);

      commit('resetContactOwnerCustomFields');
      commit('setContactOwnerCustomFields', slicedArray);
    },
    resetContactOwnerCustomFields({ commit }) {
      commit('resetContactOwnerCustomFields');
    },
    setRelationshipActions({ commit }, contactId) {
      commit('setShowCreateModal', true);
      commit('setShowRelationField', true);
      commit('setContactSelected', contactId);
    },
    resetShowCreateModal({ commit }) {
      commit('setShowCreateModal', false);
    },
    resetShowRelationField({ commit }) {
      commit('setShowRelationField', false);
    },
    async patchContactRelationships({ commit, state }, relationships) {
      const payload = {
        main_contact_id: state.contact.id,
        relationships: relationships,
      };

      const res = await axios.put(
        `${process.env.VUE_APP_CONTACTS_MS_BASE_URL}/relationships`,
        payload,
      );
    },
  },
};
